import { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, signInWithGoogle, logOut } from "./firebase";
import { useCollection } from "./firestore";
import { today, parseDate, fmt, displayDate } from "./shared/utils";
import { Icon, Avatar, Modal } from "./shared/ui.jsx";
import { ALLOWED_EMAILS, EMAIL_TO_USER } from "./shared/constants";
import ActivitiesTab, { ActivityForm } from "./components/ActivitiesTab.jsx";
import InventoryTab, { InventoryForm } from "./components/InventoryTab.jsx";
import BudgetTab, { BudgetForm } from "./components/BudgetTab.jsx";
import PartnersTab, { PartnerForm } from "./components/PartnersTab.jsx";
import GoalsTab, { GoalForm } from "./components/GoalsTab.jsx";
import EventsTab, { EventForm } from "./components/EventsTab.jsx";
import TodoTab, { TodoForm } from "./components/TodoTab.jsx";

const CSS = `
  :root {
    --bg: #f5f0e8; --paper: #faf7f2; --paper-dark: #f0ebe0;
    --ink: #2c2416; --ink70: #5a4f3a; --ink50: #8a7a60; --ink30: #b8a890; --ink20: #d4c9b5; --ink10: #e8e0d0;
    --accent: #7a5c2e;
    --font-head: 'Palatino Linotype','Book Antiqua',Palatino,Georgia,serif;
    --font-body: 'Georgia','Times New Roman',serif;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  button:focus, select:focus, input:focus, textarea:focus { outline: none; }
  select:focus, input:focus, textarea:focus { border-color: var(--accent) !important; }
  input[type=date]::-webkit-calendar-picker-indicator { opacity: 0.5; cursor: pointer; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: var(--paper-dark); }
  ::-webkit-scrollbar-thumb { background: var(--ink20); border-radius: 3px; }
`;

function LoginScreen({ onLogin, error }) {
  const [loading, setLoading] = useState(false);
  const handle = async () => { setLoading(true); try { await onLogin(); } finally { setLoading(false); } };
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--paper)", border: "1px solid var(--ink20)", borderRadius: 6, padding: "2.5rem 2rem", textAlign: "center", maxWidth: 320, width: "100%" }}>
        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.7rem", color: "var(--accent)", marginBottom: "0.4rem" }}>✦ Diary</div>
        <div style={{ fontSize: "0.82rem", color: "var(--ink50)", marginBottom: "2rem" }}>Sign in to continue</div>
        {error && <div style={{ background: "#fde8e8", color: "#b05050", borderRadius: 4, padding: "0.6rem 0.8rem", fontSize: "0.82rem", marginBottom: "1rem" }}>{error}</div>}
        <button onClick={handle} disabled={loading} style={{ width: "100%", padding: "0.75rem 1rem", border: "1px solid var(--ink20)", borderRadius: 4, background: "var(--paper-dark)", cursor: loading ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", fontFamily: "var(--font-body)", fontSize: "0.95rem", color: "var(--ink)", opacity: loading ? 0.7 : 1 }}
          onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "var(--paper)"; }}
          onMouseLeave={e => e.currentTarget.style.background = "var(--paper-dark)"}>
          <Icon name="google" size={20} />
          {loading ? "Signing in…" : "Sign in with Google"}
        </button>
        <div style={{ fontSize: "0.72rem", color: "var(--ink30)", marginTop: "1.25rem" }}>Access is restricted to invited members only.</div>
      </div>
    </div>
  );
}

// ── DataApp — only mounts after successful login ──────────────────────────────
// This ensures Firestore hooks never run before auth is ready
function DataApp({ currentUser }) {
  const [date, setDate] = useState(today);
  const [tab, setTab] = useState("activities");
  const [searching, setSearching] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [showForm, setShowForm] = useState(false);

  const [activities, activityOps] = useCollection("diary_activities");
  const [inventory,  inventoryOps] = useCollection("diary_inventory");
  const [budget,     budgetOps]    = useCollection("diary_budget");
  const [partners,   partnerOps]   = useCollection("diary_partners");
  const [goals,      goalOps]      = useCollection("diary_goals");
  const [events,     eventOps]     = useCollection("diary_events");
  const [todos,      todoOps]       = useCollection("diary_todos");

  const goDay = (d) => { const dt = parseDate(date); dt.setDate(dt.getDate() + d); setDate(fmt(dt)); };
  const q = searchQ.toLowerCase().trim();

  const TABS = [
    { id: "activities", label: "Activities", icon: "run",      render: () => <ActivitiesTab date={date} user={currentUser} activities={activities} setActivities={newItems => syncOps(activities, newItems, activityOps)} searchQ={q} /> },
    { id: "inventory",  label: "Inventory",  icon: "box",      render: () => <InventoryTab  user={currentUser} inventory={inventory}   setInventory={newItems => syncOps(inventory, newItems, inventoryOps)}   searchQ={q} /> },
    { id: "budget",     label: "Budget",     icon: "wallet",   render: () => <BudgetTab     date={date} user={currentUser} budget={budget} setBudget={newItems => syncOps(budget, newItems, budgetOps)} searchQ={q} /> },
    { id: "partners",   label: "Partners",   icon: "users",    render: () => <PartnersTab   user={currentUser} partners={partners}     setPartners={newItems => syncOps(partners, newItems, partnerOps)}     searchQ={q} /> },
    { id: "goals",      label: "Goals",      icon: "fileText", render: () => <GoalsTab      user={currentUser} goals={goals} todos={todos} setGoals={newItems => syncOps(goals, newItems, goalOps)}           searchQ={q} /> },
    { id: "events",     label: "Events",     icon: "calendar", render: () => <EventsTab     user={currentUser} events={events}         setEvents={newItems => syncOps(events, newItems, eventOps)}         searchQ={q} /> },
    { id: "todos",      label: "To-do",      icon: "check",    render: () => <TodoTab       user={currentUser} todos={todos} goals={goals}           setTodos={newItems => syncOps(todos, newItems, todoOps)}           searchQ={q} /> },
  ];

  const activeTab = TABS.find(t => t.id === tab) || TABS[0];
  const navInputStyle = { width: "auto", padding: "0.35rem 0.6rem", border: "1px solid var(--ink20)", borderRadius: 3, background: "var(--paper-dark)", color: "var(--ink)", fontFamily: "var(--font-body)", fontSize: "0.8rem", outline: "none" };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", fontFamily: "var(--font-body)", color: "var(--ink)" }}>
      <style>{CSS}</style>

      <div style={{ background: "var(--paper)", borderBottom: "1px solid var(--ink20)", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "0.65rem 1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1.15rem", color: "var(--accent)", flexShrink: 0 }}>✦ Diary</div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", flex: 1, justifyContent: "center" }}>
            <button onClick={() => goDay(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink50)", padding: "4px 6px", fontSize: "1.1rem" }}>‹</button>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={navInputStyle} />
            <button onClick={() => goDay(1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink50)", padding: "4px 6px", fontSize: "1.1rem" }}>›</button>
            <button onClick={() => setDate(today())} style={{ fontSize: "0.7rem", padding: "0.25rem 0.55rem", border: "1px solid var(--ink20)", borderRadius: 3, cursor: "pointer", background: "transparent", color: "var(--ink50)" }}>Today</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <button onClick={() => { setSearching(!searching); if (searching) setSearchQ(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink50)", padding: 4 }}>
              <Icon name="search" size={18} />
            </button>
            <button onClick={() => logOut()} title="Sign out" style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              <Avatar user={currentUser} size={28} />
            </button>
          </div>
        </div>

        {!searching && <div style={{ textAlign: "center", fontSize: "0.72rem", color: "var(--ink50)", paddingBottom: "0.4rem", letterSpacing: "0.06em" }}>{displayDate(date)} &nbsp;·&nbsp; {currentUser}</div>}
        {searching && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 1rem", background: "var(--paper-dark)", borderBottom: "1px solid var(--ink10)" }}>
            <Icon name="search" size={16} />
            <input autoFocus value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search entries…"
              style={{ flex: 1, border: "none", background: "transparent", color: "var(--ink)", fontFamily: "var(--font-body)", fontSize: "0.9rem", outline: "none" }} />
            <button onClick={() => { setSearching(false); setSearchQ(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink50)" }}>
              <Icon name="x" size={16} />
            </button>
          </div>
        )}

        <div style={{ display: "flex", borderTop: "1px solid var(--ink10)", maxWidth: 640, margin: "0 auto" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              flex: 1, padding: "0.6rem 0.5rem", border: "none", background: "transparent", cursor: "pointer",
              color: tab === t.id ? "var(--accent)" : "var(--ink50)",
              borderBottom: `2px solid ${tab === t.id ? "var(--accent)" : "transparent"}`,
              fontFamily: "var(--font-body)", fontSize: "0.82rem",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "0.35rem", transition: "all 0.15s",
            }}>
              <Icon name={t.icon} size={14} />{t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "1rem 1rem 5rem" }}>
        {activeTab.render()}
      </div>

      <button onClick={() => setShowForm(true)} style={{
        position: "fixed", bottom: "1.5rem", right: "1.5rem",
        width: 52, height: 52, borderRadius: "50%", background: "var(--accent)", color: "var(--paper)",
        border: "none", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, transition: "transform 0.15s",
      }}
        onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
        onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
      >
        <Icon name="plus" size={22} />
      </button>

      {showForm && tab === "activities" && <Modal title="New Activity / Exercise" onClose={() => setShowForm(false)}><ActivityForm date={date} user={currentUser} onSave={e  => { activityOps.add(e);  setShowForm(false); }} onClose={() => setShowForm(false)} /></Modal>}
      {showForm && tab === "inventory"  && <Modal title="New Inventory Item"      onClose={() => setShowForm(false)}><InventoryForm  user={currentUser}            onSave={i  => { inventoryOps.add(i);  setShowForm(false); }} onClose={() => setShowForm(false)} /></Modal>}
      {showForm && tab === "budget"     && <Modal title="New Budget Entry"        onClose={() => setShowForm(false)}><BudgetForm     date={date} user={currentUser} onSave={b  => { budgetOps.add(b);    setShowForm(false); }} onClose={() => setShowForm(false)} /></Modal>}
      {showForm && tab === "partners"   && <Modal title="New Partner"             onClose={() => setShowForm(false)}><PartnerForm    user={currentUser}            onSave={p  => { partnerOps.add(p);   setShowForm(false); }} onClose={() => setShowForm(false)} /></Modal>}
      {showForm && tab === "goals"      && <Modal title="New Goal"                onClose={() => setShowForm(false)}><GoalForm       user={currentUser}            onSave={g  => { goalOps.add(g);     setShowForm(false); }} onClose={() => setShowForm(false)} /></Modal>}
      {showForm && tab === "events"     && <Modal title="New Event"               onClose={() => setShowForm(false)}><EventForm      user={currentUser}            onSave={ev => { eventOps.add(ev);    setShowForm(false); }} onClose={() => setShowForm(false)} /></Modal>}
      {showForm && tab === "todos"      && <Modal title="New Task"                onClose={() => setShowForm(false)}><TodoForm       user={currentUser} goals={goals}            onSave={t  => { todoOps.add(t);     setShowForm(false); }} onClose={() => setShowForm(false)} /></Modal>}
    </div>
  );
}

// Sync helper — used by tabs that call setX([...items, newItem]) or setX(items.filter(...))
function syncOps(current, next, ops) {
  const currentIds = new Set(current.map(i => String(i.id)));
  const nextIds = new Set(next.map(i => String(i.id)));
  next.forEach(item => {
    if (!currentIds.has(String(item.id))) ops.add(item);
    else {
      const old = current.find(i => String(i.id) === String(item.id));
      if (JSON.stringify(old) !== JSON.stringify(item)) ops.update(item);
    }
  });
  current.forEach(item => { if (!nextIds.has(String(item.id))) ops.remove(item.id); });
}

// ── Auth shell — handles login, then renders DataApp ─────────────────────────
export default function App() {
  const [firebaseUser, setFirebaseUser] = useState(undefined);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => setFirebaseUser(user || null));
    return unsub;
  }, []);

  const handleLogin = async () => {
    setAuthError("");
    try {
      const result = await signInWithGoogle();
      if (!ALLOWED_EMAILS.includes(result.user.email)) {
        await logOut();
        setAuthError(`Access denied for ${result.user.email}. This app is private.`);
      }
    } catch (e) {
      console.log("Auth error:", e.code, e.message);
      if (e.code !== "auth/popup-closed-by-user" && e.code !== "auth/cancelled-popup-request") {
        setAuthError("Sign-in failed. Please try again.");
      }
    }
  };

  // Still checking auth
  if (firebaseUser === undefined) return (
    <div style={{ minHeight: "100vh", background: "#f5f0e8", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{CSS}</style>
      <div style={{ fontFamily: "'Palatino Linotype',Georgia,serif", color: "#8a7a60" }}>Loading…</div>
    </div>
  );

  const currentUser = firebaseUser ? EMAIL_TO_USER[firebaseUser.email] : null;

  // Not logged in or not on allowlist
  if (!firebaseUser || !currentUser) return (
    <div><style>{CSS}</style><LoginScreen onLogin={handleLogin} error={authError} /></div>
  );

  // Logged in — now safe to connect to Firestore
  return <DataApp currentUser={currentUser} />;
}